// Offline write queue for the lesson loop. docs/replan-v2.md R11.
//
// Scoring is server-authoritative (the client never computes a real score), so
// an attempt made offline can't be graded locally. Instead we PERSIST the raw
// request in IndexedDB and replay it (in order) when the network returns. The
// player gets an optimistic acknowledgement offline; the true score lands on
// the next sync + refetch.
//
// A tiny hand-rolled IndexedDB wrapper — no dependency. One object store
// ('pending') keyed by an auto-increment id so replay order is FIFO.

const DB_NAME = 'fa-offline';
const DB_VERSION = 1;
const STORE = 'pending';

export interface QueuedRequest {
  id?: number;
  // Absolute URL to replay against (already includes API_BASE).
  url: string;
  method: 'POST';
  body: unknown;
  // For UI/telemetry: what kind of write this is.
  kind: 'attempt' | 'complete';
  queuedAt: number;
}

// IndexedDB's `.error` is `DOMException | null`. Rejecting with a bare null
// produces an un-loggable rejection, so normalise to a real Error.
function idbError(err: DOMException | null, what: string): Error {
  return err ?? new Error(`IndexedDB ${what} failed with no error detail`);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(idbError(req.error, 'open'));
  });
}

export async function enqueue(item: Omit<QueuedRequest, 'id'>): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).add(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(idbError(tx.error, 'add'));
  });
  db.close();
}

export async function pendingCount(): Promise<number> {
  const db = await openDb();
  const count = await new Promise<number>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(idbError(req.error, 'count'));
  });
  db.close();
  return count;
}

async function allQueued(): Promise<QueuedRequest[]> {
  const db = await openDb();
  const items = await new Promise<QueuedRequest[]>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as QueuedRequest[]);
    req.onerror = () => reject(idbError(req.error, 'getAll'));
  });
  db.close();
  return items.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

async function remove(id: number): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(idbError(tx.error, 'delete'));
  });
  db.close();
}

let flushing = false;

// Replay queued writes in FIFO order. Stops at the first failure (likely still
// offline) and leaves the rest for the next attempt. Returns how many synced.
export async function flushQueue(): Promise<number> {
  if (flushing || typeof indexedDB === 'undefined') return 0;
  flushing = true;
  let synced = 0;
  try {
    const items = await allQueued();
    for (const item of items) {
      try {
        const res = await fetch(item.url, {
          method: item.method,
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(item.body),
        });
        // 2xx OR a 4xx (permanent — don't loop forever on a bad row) → drop it.
        if (res.ok || (res.status >= 400 && res.status < 500)) {
          if (item.id !== undefined) await remove(item.id);
          if (res.ok) synced++;
        } else {
          break; // 5xx / transient — retry later
        }
      } catch {
        break; // network still down
      }
    }
  } finally {
    flushing = false;
  }
  return synced;
}

// Wire up automatic flushing: on reconnect + once at startup.
export function initOfflineSync(onSynced?: (n: number) => void): void {
  if (typeof window === 'undefined') return;
  const run = () => {
    void flushQueue().then((n) => {
      if (n > 0) onSynced?.(n);
    });
  };
  window.addEventListener('online', run);
  // Best-effort initial drain (covers a reload while back online).
  run();
}
